import {
  isListItemPriority,
  type ListItemPriorityValue,
} from "./list-item-field-labels";

type PriorityGroup<T> = {
  priority: ListItemPriorityValue;
  items: T[];
};

/**
 * The buckets in list order: Not set first, so what still needs a priority is seen first (the
 * team's feedback, 2026-10-30), then Now, Next, Later (the platform's `listItemRankSql`). The
 * board's columns and the List view's sections follow it.
 */
export const PRIORITY_LIST_ORDER = [
  "NOT_SET",
  "NOW",
  "NEXT",
  "LATER",
] as const satisfies readonly ListItemPriorityValue[];

/** The bucket an item sits in: its priority, or Not set for a value the list does not know (the platform's rank puts those in Not set too). */
export function priorityBucketOf(
  priority: string | null | undefined,
): ListItemPriorityValue {
  return isListItemPriority(priority) ? priority : "NOT_SET";
}

/**
 * The four buckets in list order, each with its items in the order they came (the API's
 * `sort=rank`, so each bucket's own drag order), an empty bucket included. An unknown priority
 * sits in Not set, as in the platform's rank.
 */
export function groupByPriority<T extends { priority: string }>(
  items: readonly T[],
): PriorityGroup<T>[] {
  return PRIORITY_LIST_ORDER.map((priority) => ({
    priority,
    items: items.filter((item) => priorityBucketOf(item.priority) === priority),
  }));
}

/** Headings are worth showing unless the only bucket with items is Not set (most homes today). */
export function shouldShowPriorityHeadings(
  groups: readonly { priority: string; items: readonly unknown[] }[],
): boolean {
  const filled = groups.filter((group) => group.items.length > 0);
  return !(filled.length === 1 && filled[0]?.priority === "NOT_SET");
}
